import { ProgressCard } from "../common/ProgressCard";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CloudUpload, HelpCircle } from "lucide-react";
import { useWizard, type DocumentMetadata } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { UploadSectionIcon } from "../common/UploadSectionIcon";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { MultiSelectCombobox } from "@/components/workflow/common/MultiSelectCombobox";

const POLICY_OPTIONS = [
  "Child labour",
  "Forced labour",
  "Discrimination",
  "Freedom of association",
  "Working hours",
  "Fair wages",
  "Human trafficking",
  "Accident prevention",
];

interface HumanRightsCardProps {
  onCollapse: () => void;
}

function FieldBlock({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Label className="text-sm font-medium leading-5 text-slate-900">{label}</Label>
      {children}
    </div>
  );
}

function ChoiceCard({
  checked,
  disabled,
  label,
  name,
  id,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  name: string;
  id: string;
  onChange: () => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-[72px] flex-1 cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors",
        checked ? "border-primary/20 bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white",
          checked ? "border-primary" : "border-slate-500",
        )}
      >
        <span className={cn("h-2 w-2 rounded-full bg-primary", !checked && "opacity-0")} />
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}

function IncidentTextArea({
  id,
  label,
  placeholder,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Label htmlFor={id} className="text-sm font-medium leading-5 text-slate-900">
        {label}
      </Label>
      <Textarea
        id={id}
        placeholder={placeholder}
        className="form-input min-h-[152px] rounded-xl px-4 py-3 text-sm leading-5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}


export function HumanRightsCard({ onCollapse }: HumanRightsCardProps) {
  const {
    humanRightsData,
    updateHumanRightsData,
    humanRightsCompletion,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
  } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();

  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Your human rights data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title="Human Rights"
        description="Report on human rights policies and any incidents."
        onClose={onCollapse}
        onSave={() => {
          void handleSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag="Social"
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold leading-4 text-primary">
                C6
              </div>
              <h3 className="text-base font-semibold leading-6 text-slate-900 sm:text-lg sm:leading-7">
                Policies & Processes
              </h3>
              <HelpCircle className="h-[15px] w-[15px] text-slate-500" />
            </div>
          }
        >
          <div className="flex flex-col gap-6">
            <p className="text-sm font-medium leading-5 text-slate-900">
              Describe your human rights policies and processes.
            </p>

            <FieldBlock
              label={
                <>
                  Does your company have a human rights policy?{" "}
                  <span className="text-red-500">*</span>
                </>
              }
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ChoiceCard
                  checked={humanRightsData.hasHumanRightsPolicy === true}
                  disabled={disabled}
                  label="Yes"
                  name="hr_policy"
                  id="hr_yes"
                  onChange={() => updateHumanRightsData({ hasHumanRightsPolicy: true })}
                />
                <ChoiceCard
                  checked={humanRightsData.hasHumanRightsPolicy === false}
                  disabled={disabled}
                  label="No"
                  name="hr_policy"
                  id="hr_no"
                  onChange={() => updateHumanRightsData({ hasHumanRightsPolicy: false })}
                />
              </div>
            </FieldBlock>
            {humanRightsData.hasHumanRightsPolicy && (
              <div className="rounded-2xl border border-primary/10 bg-primary/10 p-6">
                <div className="flex flex-col gap-4">
                  <p className="text-sm font-semibold leading-5 text-slate-900">
                    What does your policy cover?
                  </p>
                  <MultiSelectCombobox
                    options={POLICY_OPTIONS}
                    value={humanRightsData.policyCovers}
                    onChange={(covers) => updateHumanRightsData({ policyCovers: covers })}
                    placeholder="Add policy coverage..."
                    disabled={disabled}
                  />
                </div>
              </div>
            )}

            <FieldBlock
              label={
                <>
                  Does your company have a grievance mechanism?{" "}
                  <span className="text-red-500">*</span>
                </>
              }
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ChoiceCard
                  checked={humanRightsData.hasGrievanceMechanism === true}
                  disabled={disabled}
                  label="Yes"
                  name="grievance_mechanism"
                  id="grievance_yes"
                  onChange={() => updateHumanRightsData({ hasGrievanceMechanism: true })}
                />
                <ChoiceCard
                  checked={humanRightsData.hasGrievanceMechanism === false}
                  disabled={disabled}
                  label="No"
                  name="grievance_mechanism"
                  id="grievance_no"
                  onChange={() => updateHumanRightsData({ hasGrievanceMechanism: false })}
                />
              </div>
            </FieldBlock>
          </div>
        </SectionCard>

        <SectionCard
          header={
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold leading-4 text-primary">
                C7
              </div>
              <h3 className="text-base font-semibold leading-6 text-slate-900 sm:text-lg sm:leading-7">
                Severe Incidents
              </h3>
              <HelpCircle className="h-[15px] w-[15px] text-slate-500" />
            </div>
          }
        >
          <div className="flex flex-col gap-8">
            <FieldBlock
              label={
                <>
                  Any confirmed severe incidents in your workforce?{" "}
                  <span className="text-red-500">*</span>
                </>
              }
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ChoiceCard
                  checked={humanRightsData.workforceIncidents === true}
                  disabled={disabled}
                  label="Yes"
                  name="workforce_incidents"
                  id="workforce_yes"
                  onChange={() => updateHumanRightsData({ workforceIncidents: true })}
                />
                <ChoiceCard
                  checked={humanRightsData.workforceIncidents === false}
                  disabled={disabled}
                  label="No"
                  name="workforce_incidents"
                  id="workforce_no"
                  onChange={() => updateHumanRightsData({ workforceIncidents: false })}
                />
              </div>

              <IncidentTextArea
                id="workforce_desc"
                label="Description"
                placeholder="Describe the incidents..."
                value={humanRightsData.workforceIncidentsDescription}
                onChange={(value) =>
                  updateHumanRightsData({ workforceIncidentsDescription: value })
                }
                disabled={disabled}
              />
            </FieldBlock>

            <FieldBlock
              label={
                <>
                  Any confirmed severe incidents in your value chain / communities / end users?{" "}
                  <span className="text-red-500">*</span>
                </>
              }
            >
              <div className="grid gap-4 md:grid-cols-2">
                <ChoiceCard
                  checked={humanRightsData.valueChainIncidents === true}
                  disabled={disabled}
                  label="Yes"
                  name="valuechain_incidents"
                  id="valuechain_yes"
                  onChange={() => updateHumanRightsData({ valueChainIncidents: true })}
                />
                <ChoiceCard
                  checked={humanRightsData.valueChainIncidents === false}
                  disabled={disabled}
                  label="No"
                  name="valuechain_incidents"
                  id="valuechain_no"
                  onChange={() => updateHumanRightsData({ valueChainIncidents: false })}
                />
              </div>

              <IncidentTextArea
                id="valuechain_desc"
                label="Description"
                placeholder="Describe the incidents..."
                value={humanRightsData.valueChainIncidentsDescription}
                onChange={(value) =>
                  updateHumanRightsData({ valueChainIncidentsDescription: value })
                }
                disabled={disabled}
              />
            </FieldBlock>
          </div>
        </SectionCard>

        <SectionCard
          collapsible={true}
          header={
            <div className="flex items-center gap-3">
              <UploadSectionIcon />
              <h3 className="text-lg font-bold leading-7 text-slate-900">
                Supporting Documents
              </h3>
            </div>
          }
        >
          <DocumentUploadSection
            fieldIdentifier="social-human-rights"
            documents={(humanRightsData.evidenceFiles || []) as DocumentMetadata[]}
            onDocumentsChange={(docs: DocumentMetadata[]) =>
              updateHumanRightsData({ evidenceFiles: docs as any })
            }
            currentTab="social"
            label="Supporting Documents"
            description="Upload evidence documents to support your answers."
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
            maxFiles={10}
            borderColor="border-slate-200"
            hideHeader
            className="rounded-[24px] border-2 border-slate-200 bg-slate-50 p-4 sm:p-10"
            emptyStateButtonClassName="rounded-[24px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            documentsListClassName="space-y-3"
            itemClassName="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
            actionButtonClassName="rounded-3xl border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            emptyStateContent={
              <div className="flex flex-col items-center text-center">
                <div className="pb-4">
                  <div className="rounded-full bg-slate-500/5 p-4">
                    <CloudUpload className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.5} />
                  </div>
                </div>
                <p className="text-base font-bold leading-6 text-slate-800">
                  Click to upload or drag and drop
                </p>
                <p className="pt-1 text-sm leading-5 text-slate-500">
                  Upload evidence documents to support your answers.
                </p>
              </div>
            }
          />
        </SectionCard>

        <TransitionMeasuresBlock topicId="human-rights" currentTab="social" disabled={disabled} />

        <ProgressCard completion={humanRightsCompletion} />
      </div>
    </div>
  );
}
